import type { Palette, Theme } from './tokens';
import { defaultTheme } from './default';

/**
 * Turning a theme into CSS, and getting it onto a page we do not own.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TOKENS DO NOT GO ON :root
 * ---------------------------------------------------------------------------
 *
 * The dashboard puts its palette on `:root`, which is correct for an
 * application that owns its document. This does not own its document. The
 * search and price widgets are mounted into a WordPress page whose theme has
 * its own opinions, and writing `--accent` onto `:root` from inside a widget
 * means a park whose theme also uses `--accent` gets whichever of us loaded
 * last. Tokens therefore land on the widget's own wrapper and inherit down from
 * there, which is exactly as far as they need to reach.
 *
 * ---------------------------------------------------------------------------
 * THREE SCHEME STATES, AND WHY THE DEFAULT IS NOT "SYSTEM"
 * ---------------------------------------------------------------------------
 *
 * The dashboard's default is "follow the operating system", because it is the
 * whole page and the page should match the machine. A widget must not make that
 * call: a dark widget in the middle of a park's light page is not dark mode, it
 * is a rendering bug with a plausible explanation. So an embedded widget
 * defaults to `light` and the host page opts into `system` or `dark`
 * explicitly, while this app — which does own its page — passes `system`.
 *
 * The three blocks are still emitted, and in the order the dashboard's own
 * comment argues for: the bare selector carries a COMPLETE palette, and the
 * other two may only redefine. A token whose sole definition sits inside a
 * media query renders one scheme's text on the other scheme's ground.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE <style> PER THEME, IN THE HEAD
 * ---------------------------------------------------------------------------
 *
 * A page may carry several widgets — a search box in the sidebar and a price
 * panel in the post — and they will normally share a theme. Keying the element
 * on the theme id means the second widget finds the first one's stylesheet and
 * mounts with no extra work, and a park that deliberately uses two themes gets
 * two elements rather than a fight.
 */

/**
 * The class every widget root carries, and the prefix every rule in
 * `styles.css` is written under. Exported because the tests audit the
 * stylesheet against it and the WordPress plugin needs it to write a
 * host-specific override.
 */
export const SCOPE = 'pmsw';

export type Scheme = 'light' | 'dark' | 'system';

function block(selector: string, colorScheme: string, tokens: Record<string, string>): string {
  const body = Object.entries(tokens).map(([k, v]) => `--${k}:${v}`).join(';');
  return `${selector}{color-scheme:${colorScheme};${body}}`;
}

/**
 * A theme as a stylesheet.
 *
 * Pure and exported so a test can read it, and so a park can generate a static
 * `.css` file at build time rather than paying for the injection at runtime.
 */
export function themeCss(theme: Theme, scope: string = SCOPE): string {
  const root = `.${scope}`;
  // Shape rides along with the light palette in the bare block: it does not
  // vary by scheme, and stating it once is what stops a dark theme quietly
  // inheriting a radius its light half changed.
  const base = { ...theme.shape, ...theme.light } as unknown as Record<string, string>;
  const dark = theme.dark as unknown as Record<string, string>;
  return [
    block(root, 'light', base),
    // Only when the host asked for it. See the header.
    `@media (prefers-color-scheme:dark){${block(`${root}[data-scheme="system"]`, 'dark', dark)}}`,
    block(`${root}[data-scheme="dark"]`, 'dark', dark),
  ].join('\n');
}

/** The element id a theme's stylesheet is kept under. */
export function themeStyleId(theme: Theme): string {
  return `pms-theme-${theme.id}`;
}

/**
 * Put a theme on the page, once.
 *
 * Idempotent: mounting five widgets with the same theme leaves one `<style>`.
 * Re-running with an edited theme of the same id rewrites it, which is what
 * makes a theme switcher in a test or a story cost nothing.
 */
export function installTheme(theme: Theme = defaultTheme, doc: Document = document): void {
  const id = themeStyleId(theme);
  let el = doc.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = doc.createElement('style');
    el.id = id;
    el.setAttribute('data-pms-theme', theme.id);
    doc.head.appendChild(el);
  }
  const css = themeCss(theme);
  // Compared before writing: assigning identical text still invalidates style
  // in some engines, and a widget that re-installs on every render would then
  // restyle the host page on every render too.
  if (el.textContent !== css) el.textContent = css;
}

/** Every token name a theme defines. Used by the stylesheet audit. */
export function tokenNames(theme: Theme): Set<string> {
  return new Set([
    ...Object.keys(theme.shape),
    ...Object.keys(theme.light),
    ...Object.keys(theme.dark),
  ]);
}

/** How many tokens two themes actually disagree about. */
export function tokenDifference(a: Theme, b: Theme): number {
  const pick = (t: Theme) => ({
    ...(t.shape as unknown as Record<string, string>),
    ...(t.light as unknown as Record<string, string>),
  });
  const left = pick(a);
  const right = pick(b);
  return Object.keys(left).filter((k) => left[k] !== right[k]).length;
}

export type { Palette, Theme };
